import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { alpha, type Theme } from '@mui/material/styles';

import { HeaderBreadcrumbs, Iconify, Page, Scrollbar } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { TableHeadCustom, TableNoData } from '@/components/table';
import {
  createChatAccessGroup,
  deleteChatAccessGroup,
  getChatAccessGroups,
  updateChatAccessGroup,
} from '@/features/chat-stream/api';
import type {
  ChatAccessGroup,
  ChatAccessGroupMember,
  ChatAccessGroupPayload,
  ChatChannelType,
} from '@/features/chat-stream/types';
import { PATH_USER } from '@/features/app';
import { getDashboardUsers } from '@/features/users/shared/api';
import type { User } from '@/features/users/shared/types';
import { useGetAccess } from '@/hooks/useAccess';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';

type ChatAccessGroupFormState = {
  active: boolean;
  channel_types: ChatChannelType[];
  description: string;
  member_user_ids: number[];
  name: string;
};

type ChatAccessGroupFormErrors = Partial<
  Record<keyof ChatAccessGroupFormState, string>
>;

type ChatChannelTypeOption = {
  description: string;
  label: string;
  value: ChatChannelType;
};

type ChatAccessGroupFormDrawerProps = {
  currentGroup: ChatAccessGroup | null;
  isSubmitting: boolean;
  onClose: VoidFunction;
  onSubmitGroup: (payload: ChatAccessGroupPayload) => Promise<void>;
  open: boolean;
  userOptions: ChatAccessGroupMember[];
};

const CHAT_ACCESS_GROUPS_QUERY_KEY = ['chatAccessGroups'];
const DASHBOARD_USERS_QUERY_KEY = ['dashboardUsers'];

const CHANNEL_TYPE_OPTIONS: ChatChannelTypeOption[] = [
  {
    description: 'Dashboard user-to-user chats',
    label: 'Dashboard user',
    value: 'dashboardSupport',
  },
  {
    description: 'Client and selected dashboard users',
    label: 'Client support',
    value: 'clientSupport',
  },
  {
    description: 'Cleaner and selected dashboard users',
    label: 'Cleaner support',
    value: 'cleanerSupport',
  },
  {
    description: 'Client, cleaner(s), and selected dashboard users',
    label: 'Job support',
    value: 'jobSupport',
  },
];

const TABLE_HEAD = [
  { id: 'name', label: 'Name', align: 'left', minWidth: 220 },
  { id: 'channel_types', label: 'Channel types', align: 'left', minWidth: 220 },
  { id: 'members', label: 'Members', align: 'left', minWidth: 280 },
  { id: 'active', label: 'Status', align: 'left', minWidth: 120 },
  { id: 'actions', label: '', align: 'right', minWidth: 180 },
];

const DEFAULT_FORM_STATE: ChatAccessGroupFormState = {
  active: true,
  channel_types: [],
  description: '',
  member_user_ids: [],
  name: '',
};

const getGroupMembers = (group: ChatAccessGroup): ChatAccessGroupMember[] =>
  group.members ??
  group.member_users ??
  group.member_dashboard_users ??
  group.dashboard_users ??
  group.users ??
  [];

const getGroupMemberIds = (group: ChatAccessGroup) => {
  if (group.member_user_ids?.length) {
    return group.member_user_ids;
  }

  return getGroupMembers(group).map((member) => member.id);
};

const getUserName = (user: Partial<User> | ChatAccessGroupMember) => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || `User ${user.id}`;
};

const isChatUserAvailable = (user: ChatAccessGroupMember) =>
  Boolean(user.is_chat_user && user.chat_user_id);

const getChannelTypeLabel = (value: ChatChannelType) =>
  CHANNEL_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;

const getMemberChipSx = (available: boolean) => (theme: Theme) => {
  const color = available ? theme.palette.primary : theme.palette.warning;

  return {
    bgcolor: alpha(color.main, available ? 0.12 : 0.16),
    border: `1px solid ${alpha(color.main, available ? 0.34 : 0.42)}`,
    color: color.dark,
    fontWeight: 700,
    height: 28,
    '& .MuiChip-label': {
      px: 1.25,
    },
  };
};

const buildFormState = (
  group?: ChatAccessGroup | null
): ChatAccessGroupFormState => {
  if (!group) return DEFAULT_FORM_STATE;

  return {
    active: group.active,
    channel_types: group.channel_types ?? [],
    description: group.description ?? '',
    member_user_ids: getGroupMemberIds(group),
    name: group.name ?? '',
  };
};

const mergeUsers = (
  dashboardUsers: User[] = [],
  groups: ChatAccessGroup[] = []
): ChatAccessGroupMember[] => {
  const userMap = new Map<number, ChatAccessGroupMember>();

  dashboardUsers.forEach((user) => {
    userMap.set(user.id, {
      chat_user_id: user.chat_user_id,
      email: user.email,
      first_name: user.first_name,
      id: user.id,
      is_chat_user: user.is_chat_user,
      last_name: user.last_name,
      profile_image: user.profile_image,
    });
  });

  groups.forEach((group) => {
    getGroupMembers(group).forEach((member) => {
      if (!userMap.has(member.id)) {
        userMap.set(member.id, member);
      }
    });
  });

  return Array.from(userMap.values()).sort((a, b) =>
    getUserName(a).localeCompare(getUserName(b))
  );
};

const buildPayload = (
  formState: ChatAccessGroupFormState
): ChatAccessGroupPayload => ({
  active: formState.active,
  channel_types: formState.channel_types,
  description: formState.description.trim(),
  member_user_ids: formState.member_user_ids,
  name: formState.name.trim(),
});

function ChatAccessGroupFormDrawer({
  currentGroup,
  isSubmitting,
  onClose,
  onSubmitGroup,
  open,
  userOptions,
}: ChatAccessGroupFormDrawerProps) {
  const [formState, setFormState] =
    useState<ChatAccessGroupFormState>(DEFAULT_FORM_STATE);
  const [errors, setErrors] = useState<ChatAccessGroupFormErrors>({});
  const selectedChannelTypeOptions = CHANNEL_TYPE_OPTIONS.filter((option) =>
    formState.channel_types.includes(option.value)
  );
  const selectedUsers = userOptions.filter((user) =>
    formState.member_user_ids.includes(user.id)
  );
  const isEdit = Boolean(currentGroup);

  useEffect(() => {
    if (open) {
      setFormState(buildFormState(currentGroup));
      setErrors({});
    }
  }, [currentGroup, open]);

  const setField = <T extends keyof ChatAccessGroupFormState>(
    field: T,
    value: ChatAccessGroupFormState[T]
  ) => {
    setFormState((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const validate = () => {
    const nextErrors: ChatAccessGroupFormErrors = {};

    if (!formState.name.trim()) {
      nextErrors.name = 'Name is required';
    }

    if (!formState.channel_types.length) {
      nextErrors.channel_types = 'Select at least one channel type';
    }

    if (!formState.member_user_ids.length) {
      nextErrors.member_user_ids = 'Select at least one dashboard user';
    }

    setErrors(nextErrors);
    return !Object.keys(nextErrors).length;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validate()) return;

    await onSubmitGroup(buildPayload(formState));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: 1, sm: 560 }, maxWidth: 1 },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', height: 1 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2 }}
        >
          <Box>
            <Typography variant="h6">
              {isEdit ? 'Edit chat access group' : 'New chat access group'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose which dashboard users are added to each chat type.
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>

        <Divider />

        <Stack spacing={2.5} sx={{ px: 3, py: 3, flex: 1, overflowY: 'auto' }}>
          <TextField
            label="Name"
            value={formState.name}
            onChange={(event) => setField('name', event.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
            fullWidth
          />

          <TextField
            label="Description"
            value={formState.description}
            onChange={(event) => setField('description', event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />

          <Autocomplete
            multiple
            options={CHANNEL_TYPE_OPTIONS}
            value={selectedChannelTypeOptions}
            onChange={(_event, value) =>
              setField(
                'channel_types',
                value.map((option) => option.value)
              )
            }
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <Stack spacing={0.25}>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Stack>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Channel types"
                error={Boolean(errors.channel_types)}
                helperText={errors.channel_types}
              />
            )}
          />

          <Autocomplete
            multiple
            options={userOptions}
            value={selectedUsers}
            onChange={(_event, value) =>
              setField(
                'member_user_ids',
                value.map((user) => user.id)
              )
            }
            getOptionDisabled={(option) =>
              !isChatUserAvailable(option) &&
              !formState.member_user_ids.includes(option.id)
            }
            getOptionLabel={getUserName}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack spacing={0.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">
                      {getUserName(option)}
                    </Typography>
                    {!isChatUserAvailable(option) && (
                      <Chip
                        color="warning"
                        label="Chat unavailable"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  {option.email && (
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  )}
                </Stack>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={getUserName(option)}
                  size="small"
                  color={isChatUserAvailable(option) ? 'primary' : 'warning'}
                  variant={isChatUserAvailable(option) ? 'filled' : 'outlined'}
                  sx={getMemberChipSx(isChatUserAvailable(option))}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Dashboard users"
                error={Boolean(errors.member_user_ids)}
                helperText={
                  errors.member_user_ids ??
                  'Only chat-enabled dashboard users can be added to new groups.'
                }
              />
            )}
          />

          <Alert severity="info">
            The API will add these dashboard users whenever a matching chat is
            opened or reconciled.
          </Alert>

          <FormControlLabel
            control={
              <Switch
                checked={formState.active}
                onChange={(event) => setField('active', event.target.checked)}
              />
            }
            label={formState.active ? 'Active' : 'Inactive'}
          />
        </Stack>

        <Divider />

        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{ px: 3, py: 2 }}
        >
          <Button color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting}
            type="submit"
            variant="contained"
          >
            {isEdit ? 'Save changes' : 'Create group'}
          </LoadingButton>
        </Stack>
      </Box>
    </Drawer>
  );
}

export default function ChatAccessGroupsList() {
  const { themeStretch } = useSettings();
  const permissions = useGetAccess('chat_access_groups');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState<ChatAccessGroup | null>(
    null
  );
  const [groupToDeactivate, setGroupToDeactivate] =
    useState<ChatAccessGroup | null>(null);

  const groupsQuery = useQuery({
    queryKey: CHAT_ACCESS_GROUPS_QUERY_KEY,
    queryFn: getChatAccessGroups,
    onError: (error) => {
      errorMsg(error, 'Unable to load chat access groups');
    },
  });

  const dashboardUsersQuery = useQuery({
    queryKey: DASHBOARD_USERS_QUERY_KEY,
    queryFn: getDashboardUsers,
    onError: (error) => {
      errorMsg(error, 'Unable to load dashboard users');
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: createChatAccessGroup,
    onSuccess: () => {
      successMsg('Chat access group created');
      queryClient.invalidateQueries(CHAT_ACCESS_GROUPS_QUERY_KEY);
      setFormOpen(false);
      setCurrentGroup(null);
    },
    onError: (error) => {
      errorMsg(error, 'Unable to create chat access group');
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: ChatAccessGroupPayload;
    }) => updateChatAccessGroup(id, payload),
    onSuccess: () => {
      successMsg('Chat access group updated');
      queryClient.invalidateQueries(CHAT_ACCESS_GROUPS_QUERY_KEY);
      setFormOpen(false);
      setCurrentGroup(null);
    },
    onError: (error) => {
      errorMsg(error, 'Unable to update chat access group');
    },
  });

  const deactivateGroupMutation = useMutation({
    mutationFn: (group: ChatAccessGroup) => deleteChatAccessGroup(group.id),
    onSuccess: () => {
      successMsg('Chat access group deactivated');
      queryClient.invalidateQueries(CHAT_ACCESS_GROUPS_QUERY_KEY);
      setGroupToDeactivate(null);
    },
    onError: (error) => {
      errorMsg(error, 'Unable to deactivate chat access group');
    },
  });

  const groups = groupsQuery.data ?? [];
  const userOptions = useMemo(
    () => mergeUsers(dashboardUsersQuery.data, groups),
    [dashboardUsersQuery.data, groups]
  );
  const filteredGroups = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    if (!searchValue) return groups;

    return groups.filter((group) => {
      const memberText = getGroupMembers(group).map(getUserName).join(' ');
      const channelTypeText = (group.channel_types ?? [])
        .map(getChannelTypeLabel)
        .join(' ');

      return [group.name, group.description, memberText, channelTypeText]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(searchValue);
    });
  }, [groups, search]);
  const isLoading = groupsQuery.isLoading || dashboardUsersQuery.isLoading;
  const isSubmitting =
    createGroupMutation.isLoading || updateGroupMutation.isLoading;

  const handleCreateGroup = () => {
    setCurrentGroup(null);
    setFormOpen(true);
  };

  const handleEditGroup = (group: ChatAccessGroup) => {
    setCurrentGroup(group);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setCurrentGroup(null);
  };

  const handleSubmitGroup = async (payload: ChatAccessGroupPayload) => {
    if (currentGroup) {
      await updateGroupMutation.mutateAsync({ id: currentGroup.id, payload });
      return;
    }

    await createGroupMutation.mutateAsync(payload);
  };

  const handleConfirmDeactivate = () => {
    if (!groupToDeactivate) return;
    deactivateGroupMutation.mutate(groupToDeactivate);
  };

  return (
    <Page title="Chat Access Groups">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Chat Access Groups"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Settings' },
            { name: 'Chat Access Groups' },
          ]}
          action={
            permissions.canCreate && (
              <Button
                variant="contained"
                onClick={handleCreateGroup}
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                Add group
              </Button>
            )
          }
        />

        <Card>
          <Grid container spacing={2} sx={{ py: 2.5, px: 3 }}>
            <Grid item xs={12} md={5}>
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                fullWidth
                size="small"
                placeholder="Search groups, members, or channel types"
                InputProps={{
                  startAdornment: (
                    <Iconify
                      icon="eva:search-outline"
                      width={20}
                      sx={{ mr: 1, color: 'text.disabled' }}
                    />
                  ),
                }}
              />
            </Grid>
          </Grid>

          {isLoading ? (
            <SkeletonLoadingTable />
          ) : (
            <Scrollbar>
              <TableContainer sx={{ minWidth: 920 }}>
                <Table>
                  <TableHeadCustom headLabel={TABLE_HEAD} />
                  <TableBody>
                    {filteredGroups.map((group) => {
                      const members = getGroupMembers(group);

                      return (
                        <TableRow hover key={group.id}>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography variant="subtitle2">
                                {group.name}
                              </Typography>
                              {group.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {group.description}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              flexWrap="wrap"
                            >
                              {(group.channel_types ?? []).map((type) => (
                                <Chip
                                  key={type}
                                  label={getChannelTypeLabel(type)}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              flexWrap="wrap"
                            >
                              {members.length ? (
                                <>
                                  {members.slice(0, 4).map((member) => (
                                    <Chip
                                      key={member.id}
                                      label={getUserName(member)}
                                      size="small"
                                      color={
                                        isChatUserAvailable(member)
                                          ? 'default'
                                          : 'warning'
                                      }
                                      variant={
                                        isChatUserAvailable(member)
                                          ? 'filled'
                                          : 'outlined'
                                      }
                                      sx={getMemberChipSx(
                                        isChatUserAvailable(member)
                                      )}
                                    />
                                  ))}
                                  {members.length > 4 && (
                                    <Chip
                                      label={`+${members.length - 4}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No members
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={group.active ? 'Active' : 'Inactive'}
                              size="small"
                              color={group.active ? 'success' : 'default'}
                              variant={group.active ? 'filled' : 'outlined'}
                            />
                          </TableCell>

                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.75}
                              justifyContent="flex-end"
                            >
                              {permissions.canUpdate && (
                                <Tooltip title="Edit group">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditGroup(group)}
                                  >
                                    <Iconify icon="eva:edit-fill" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {permissions.canDelete && group.active && (
                                <Tooltip title="Deactivate group">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setGroupToDeactivate(group)}
                                  >
                                    <Iconify icon="eva:trash-2-outline" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    <TableNoData
                      isNotFound={!filteredGroups.length}
                      title={
                        search
                          ? 'No chat access groups found'
                          : 'No chat access groups available'
                      }
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>

      <ChatAccessGroupFormDrawer
        open={formOpen}
        currentGroup={currentGroup}
        userOptions={userOptions}
        isSubmitting={isSubmitting}
        onClose={handleCloseForm}
        onSubmitGroup={handleSubmitGroup}
      />

      <DialogSimple
        dialogTitle="Deactivate chat access group?"
        open={Boolean(groupToDeactivate)}
        onClickButtonClose={() => setGroupToDeactivate(null)}
        loadingButton
        loadingButtonText="Deactivate"
        loadingButtonColor="error"
        isSubmitting={deactivateGroupMutation.isLoading}
        onClickButtonLoading={handleConfirmDeactivate}
      >
        <Stack spacing={1.5} sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2">
            This will make the group inactive. Users in the group will no longer
            be managed into newly opened matching chats.
          </Typography>
          <Typography variant="subtitle2">{groupToDeactivate?.name}</Typography>
        </Stack>
      </DialogSimple>
    </Page>
  );
}
